import { Injectable } from '@angular/core';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Auth } from 'aws-amplify';
import { catchError, map, of, switchMap, withLatestFrom } from 'rxjs';

import {
  PageProductSortimentPageResponse,
  ProductSortimentCreateResponse,
  ProductSortimentPageResponse,
} from '@app/api/marketplace/models';
import {
  ProductSortimentsService,
  ProductTransfersService,
} from '@app/api/marketplace/services';
import { MerchantActions } from '@merchant/store/merchant.slice';
import { SnackbarComponent } from '@shared/components/snackbar/snackbar.component';
import { paramConvert } from '@shared/helpers/service-helpers';
import { PageResponse } from '@shared/interfaces/page-response.interface';
import { DialogService } from '@shared/services/dialog.service';

import {
  ProductSortimentActions,
  ProductSortimentSelectors,
} from './product-sortiment.slice';

@Injectable({ providedIn: 'root' })
export class ProductSortimentEffects {
  loadProductSortiment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductSortimentActions.loadProductSortiment.trigger),
      withLatestFrom(
        this.store.select(
          ProductSortimentSelectors.selectProductSortimentFilter
        )
      ),
      switchMap(([_action, filter]) => {
        const params = {
          page: filter.page,
          size: filter.size,
          sort: filter.sort,
        };
        const { page: _, size: __, sort: ___, ...body } = filter;
        return this.productSortimentsService
          .getProductSortiment({
            ...params,
            body: paramConvert(body),
          })
          .pipe(
            map((response: PageProductSortimentPageResponse) =>
              ProductSortimentActions.loadProductSortiment.success({
                response:
                  response as PageResponse<ProductSortimentPageResponse>,
              })
            ),
            catchError(() =>
              of(ProductSortimentActions.loadProductSortiment.failure())
            )
          );
      })
    )
  );

  addProductToSortiment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductSortimentActions.addProductToSortiment.trigger),
      withLatestFrom(
        this.store.select(ProductSortimentSelectors.selectPackagingUnitId)
      ),
      switchMap(([_action, packagingUnitId]) =>
        this.productSortimentsService
          .addProductToSortiment({ body: { packagingUnitId } })
          .pipe(
            map((response: ProductSortimentCreateResponse) => {
              this.snackBar.openFromComponent(SnackbarComponent, {
                horizontalPosition: 'center',
                verticalPosition: 'bottom',
                duration: 3000,
                data: this.translateService.instant(
                  'SHARED.SORTIMENT_ADDED_SUCCES'
                ),
              });
              return ProductSortimentActions.addProductToSortiment.success(
                response
              );
            })
          )
      )
    )
  );

  deleteProductSortiment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductSortimentActions.deleteProductSortiment.trigger),
      switchMap((action) => {
        return this.productSortimentsService
          .deleteProductFromSortiment({ prodSortId: action.productSortimentId })
          .pipe(
            map(() => {
              this.store.dispatch(
                ProductSortimentActions.loadProductSortiment.trigger()
              );
              this.store.dispatch(MerchantActions.loadBasicData.trigger());
              return ProductSortimentActions.deleteProductSortiment.success();
            })
          );
      })
    )
  );

  updateStoredFilter$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductSortimentActions.updateStoredFilter.trigger),
      switchMap((_action) => Auth.currentSession()),
      switchMap((session) => {
        return of(ProductSortimentActions.updateStoredFilter.success()).pipe(
          map(() => {
            if (
              !session
                .getIdToken()
                .payload['cognito:groups'].includes('IMPERSONATION')
            ) {
              this.store.dispatch(
                ProductSortimentActions.loadProductSortiment.trigger()
              );
            }

            return ProductSortimentActions.updateStoredFilter.success();
          })
        );
      })
    )
  );

  createProductTransfer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductSortimentActions.createProductTransfer.trigger),
      switchMap((action) => {
        return this.productTransfersService
          .createProductTransferRequests({
            body: action.request,
          })
          .pipe(
            map(() => {
              this.dialogService.open({
                content: 'DIALOG.TAKOVER_DIALOG_CONTENT',
                title: 'DIALOG.TAKOVER_DIALOG_TITLE',
                type: 'NOTICE',
              });
              return ProductSortimentActions.createProductTransfer.success();
            })
          );
      })
    )
  );

  constructor(
    private readonly store: Store,
    private readonly actions$: Actions,
    private readonly productSortimentsService: ProductSortimentsService,
    private readonly snackBar: MatSnackBar,
    private readonly dialogService: DialogService,
    private readonly productTransfersService: ProductTransfersService,
    private translateService: TranslateService
  ) {}
}
